// The chrome (SPEC §15): the parts every panel is built out of.
//
// "Thin hairline rules, nested rectangles with corner brackets rather than filled
// panels, tick-marked rails along the map edges, small rotating radial dials for
// live values, and a targeting reticle that snaps to the hovered pixel. Panels
// are translucent and blurred over the map, never opaque — the war stays visible
// behind the UI at all times."
//
// Each of those is one component here, and each is a styled Tamagui component so
// the compiler can flatten it — which §15 asks for by name, because "the UI
// shares a frame budget with a WebGL canvas running tracer animation;
// per-render style computation is the thing that would show up first as jank on
// a contested front".
//
// The type rule is enforced structurally rather than by review: `Readout` is
// mono with tabular figures and cannot be given a family, and `UserText` is sans
// and cannot be given one either. A component that could be either is a component
// that will eventually render a player's comment in the theme font.

import { Stack, Text, XStack, YStack, styled } from 'tamagui';
import type { ReactNode } from 'react';
import { PanelClass, ScanClass, TabularClass } from './css';
import { SectionLabels, type SectionKey } from './labels';

/**
 * A hairline. One device pixel of the line colour, and nothing else.
 *
 * `space.hair` is 1 in the token file rather than a literal here, so a display
 * with a different idea of a hairline is one token away.
 */
export const Hairline = styled(Stack, {
  name: 'Hairline',
  backgroundColor: '$hairline',
  height: '$hair',
  alignSelf: 'stretch',
});

export const VHairline = styled(Stack, {
  name: 'VHairline',
  backgroundColor: '$hairlineFaint',
  width: '$hair',
  alignSelf: 'stretch',
});

/**
 * The panel body: translucent, blurred, square, hairline-bordered.
 *
 * `backgroundColor` is the token's rgba value, not a solid with an opacity prop,
 * because opacity would fade the CONTENT as well and §15 wants the text crisp
 * over a blurred map, not the whole panel at 70%.
 */
export const PanelFrame = styled(YStack, {
  name: 'PanelFrame',
  backgroundColor: '$panelFill',
  borderColor: '$hairline',
  borderWidth: 1,
  borderRadius: 0,
  padding: '$sm',
  gap: '$xs',
});

/** One corner bracket. Two borders, no fill — the film's own frame. */
const Bracket = styled(Stack, {
  name: 'Bracket',
  position: 'absolute',
  width: 10,
  height: 10,
  borderColor: '$bracket',
  pointerEvents: 'none',
  variants: {
    corner: {
      tl: { top: -1, left: -1, borderTopWidth: 1, borderLeftWidth: 1 },
      tr: { top: -1, right: -1, borderTopWidth: 1, borderRightWidth: 1 },
      bl: { bottom: -1, left: -1, borderBottomWidth: 1, borderLeftWidth: 1 },
      br: { bottom: -1, right: -1, borderBottomWidth: 1, borderRightWidth: 1 },
    },
  } as const,
});

export function CornerBrackets(): ReactNode {
  return (
    <>
      <Bracket corner="tl" />
      <Bracket corner="tr" />
      <Bracket corner="bl" />
      <Bracket corner="br" />
    </>
  );
}

/** Interface text: monospace, tabular figures, drained. */
export const Readout = styled(Text, {
  name: 'Readout',
  fontFamily: '$mono',
  color: '$text',
  fontSize: '$3',
  lineHeight: '$3',
  letterSpacing: '$3',
  className: TabularClass,
  variants: {
    tone: {
      dim: { color: '$textDim' },
      primary: { color: '$cyan' },
      data: { color: '$phosphor' },
      warn: { color: '$amber' },
      alert: { color: '$alert' },
    },
    level: {
      small: { fontSize: '$1', lineHeight: '$1', letterSpacing: '$1' },
      body: { fontSize: '$3', lineHeight: '$3', letterSpacing: '$3' },
      large: { fontSize: '$6', lineHeight: '$6', letterSpacing: '$6' },
      hero: { fontSize: '$8', lineHeight: '$8', letterSpacing: '$8' },
    },
  } as const,
});

/**
 * Anything a person wrote: comments, country names as the player typed them,
 * appeal text.
 *
 * §15: "render in a clean sans, unstyled, because dressing user text in the theme
 * makes it illegible." Unstyled means what it says — no letter spacing, no
 * uppercase transform, no tabular figures, no katakana anywhere near it.
 */
export const UserText = styled(Text, {
  name: 'UserText',
  fontFamily: '$sans',
  color: '$text',
  fontSize: '$4',
  lineHeight: '$4',
  letterSpacing: 0,
  className: 'wwp-user',
});

/**
 * A section header: Latin primary, katakana secondary.
 *
 * Keyed by section rather than taking a free string, so no caller can put user
 * content through the katakana channel.
 */
export function SectionHeader({ section, right }: { section: SectionKey; right?: ReactNode }): ReactNode {
  const label = SectionLabels[section];
  return (
    <XStack alignItems="baseline" gap="$sm">
      <Readout tone="primary" level="small">
        {label.latin}
      </Readout>
      <Readout tone="dim" level="small" opacity={0.7}>
        {label.kana}
      </Readout>
      <Stack flex={1} />
      {right}
    </XStack>
  );
}

/** A panel: frame, brackets, scanline sweep, header. */
export function Panel({
  section,
  children,
  right,
  width,
}: {
  section?: SectionKey;
  children?: ReactNode;
  right?: ReactNode;
  width?: number;
}): ReactNode {
  return (
    <PanelFrame className={`${PanelClass} ${ScanClass}`} width={width}>
      <CornerBrackets />
      {section ? <SectionHeader section={section} right={right} /> : null}
      {section ? <Hairline /> : null}
      {children}
    </PanelFrame>
  );
}

/**
 * A tick-marked rail.
 *
 * Along the map edges, and inside panels as the ruler under a bar. Every fifth
 * tick is long, which is what turns decoration into something you can actually
 * read a value off.
 */
export function Rail({
  count = 24,
  vertical = false,
}: {
  count?: number;
  vertical?: boolean;
}): ReactNode {
  const ticks = [];
  for (let i = 0; i < count; i++) {
    const major = i % 5 === 0;
    ticks.push(
      <Stack
        key={i}
        backgroundColor={major ? '$hairline' : '$hairlineFaint'}
        width={vertical ? (major ? 7 : 4) : 1}
        height={vertical ? 1 : major ? 7 : 4}
      />,
    );
  }
  return vertical ? (
    <YStack justifyContent="space-between" alignItems="flex-start" flex={1} pointerEvents="none">
      {ticks}
    </YStack>
  ) : (
    <XStack justifyContent="space-between" alignItems="flex-end" flex={1} pointerEvents="none">
      {ticks}
    </XStack>
  );
}

/**
 * A small radial dial for a live value.
 *
 * SVG rather than a Tamagui component: an arc is a path, and drawing one out of
 * nested rectangles would be worse in every way. The sweep is linear — §15's
 * motion rule holds for the dial as much as for the panels.
 */
export function RadialDial({
  value,
  size = 34,
  tone = 'primary',
  label,
}: {
  /** 0..1. */
  value: number;
  size?: number;
  tone?: 'primary' | 'data' | 'warn' | 'alert';
  label?: string;
}): ReactNode {
  const v = value < 0 ? 0 : value > 1 ? 1 : value;
  const stroke = 2;
  const r = size / 2 - stroke;
  const circumference = 2 * Math.PI * r;
  const colours: Record<string, string> = {
    primary: 'var(--color-cyan)',
    data: 'var(--color-phosphor)',
    warn: 'var(--color-amber)',
    alert: 'var(--color-alert)',
  };
  return (
    <Stack width={size} height={size} alignItems="center" justifyContent="center">
      <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} aria-hidden="true">
        <circle
          cx={size / 2}
          cy={size / 2}
          r={r}
          fill="none"
          stroke="var(--color-hairlineFaint)"
          strokeWidth={stroke}
        />
        <circle
          cx={size / 2}
          cy={size / 2}
          r={r}
          fill="none"
          stroke={colours[tone]}
          strokeWidth={stroke}
          strokeDasharray={`${circumference * v} ${circumference}`}
          transform={`rotate(-90 ${size / 2} ${size / 2})`}
          strokeLinecap="butt"
        />
      </svg>
      {label ? (
        <Stack position="absolute">
          <Readout level="small" tone="dim">
            {label}
          </Readout>
        </Stack>
      ) : null}
    </Stack>
  );
}

/**
 * A horizontal bar. The bank meter, the fortress HP, the season clock.
 *
 * Square, hairline-bordered, and it fills from the left with no transition: §15's
 * motion is "mechanical and linear", and an animated bar that eases into place is
 * lying about when the value changed.
 */
export function Bar({
  value,
  tone = 'data',
  height = 6,
}: {
  value: number;
  tone?: 'primary' | 'data' | 'warn' | 'alert';
  height?: number;
}): ReactNode {
  const v = value < 0 ? 0 : value > 1 ? 1 : value;
  const fill = {
    primary: '$cyan',
    data: '$phosphor',
    warn: '$amber',
    alert: '$alert',
  } as const;
  return (
    <Stack
      height={height}
      borderColor="$hairline"
      borderWidth={1}
      backgroundColor="$panelFillDeep"
      alignSelf="stretch"
    >
      <Stack backgroundColor={fill[tone]} width={`${v * 100}%`} height="100%" />
    </Stack>
  );
}
